import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Circle, Clock, ArrowRight, FileText, AlertCircle, Loader2, Undo2 } from "lucide-react";
import { useMatterStages, useDocuments } from "@/hooks/use-supabase-queries";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { format } from "date-fns";

const statusConfig = {
  completed: {
    icon: CheckCircle2,
    iconClass: "text-primary bg-primary/10",
    lineClass: "bg-primary",
    badge: { label: "Completed", className: "bg-primary/10 text-primary" },
  },
  "in-progress": {
    icon: Clock,
    iconClass: "text-accent bg-accent/10",
    lineClass: "bg-accent",
    badge: { label: "In Progress", className: "bg-accent text-accent-foreground" },
  },
  "not-started": {
    icon: Circle,
    iconClass: "text-muted-foreground bg-muted",
    lineClass: "bg-muted",
    badge: { label: "Not Started", className: "bg-muted text-muted-foreground" },
  },
};

interface MatterTimelineProps {
  matterId: string;
  onStageClick: (stageId: string) => void;
}

export function MatterTimeline({ matterId, onStageClick }: MatterTimelineProps) {
  const { data: stages, isLoading } = useMatterStages(matterId);
  const { data: documents } = useDocuments({ matter_id: matterId });
  const queryClient = useQueryClient();

  const [confirmDialog, setConfirmDialog] = useState<{ type: 'complete' | 'revert'; stageId: string; stageName: string; nextName?: string } | null>(null);
  const [notes, setNotes] = useState('');
  const [clientExplanation, setClientExplanation] = useState('');
  const [isAdvancing, setIsAdvancing] = useState(false);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-8 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const sortedStages = (stages || []).sort((a, b) => a.sort_order - b.sort_order);
  const getDocCountForStage = (stageId: string) =>
    documents?.filter(d => d.stage_id === stageId).length || 0;

  // Find the last completed stage (for revert)
  const lastCompletedIndex = sortedStages.map(s => s.status).lastIndexOf('completed');

  const handleAdvance = async () => {
    if (!confirmDialog) return;
    setIsAdvancing(true);
    try {
      const { data, error } = await supabase.functions.invoke('advance-stage', {
        body: {
          matter_id: matterId,
          stage_id: confirmDialog.stageId,
          action: confirmDialog.type,
          internal_notes: notes || undefined,
          client_explanation: clientExplanation || undefined,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      if (confirmDialog.type === 'complete') {
        if (data.completed) {
          toast.success("🎉 Matter Completed!", { description: "All stages have been completed. Parties have been notified." });
        } else {
          toast.success("Stage completed", { description: `Advanced to "${data.next_stage}". Parties notified.` });
        }
      } else {
        toast.success("Stage reverted", { description: `"${data.reverted_stage}" is back to in-progress.` });
      }

      queryClient.invalidateQueries({ queryKey: ['matter_stages'] });
      queryClient.invalidateQueries({ queryKey: ['matters'] });
      queryClient.invalidateQueries({ queryKey: ['audit_log'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      setConfirmDialog(null);
      setNotes('');
      setClientExplanation('');
    } catch (err: any) {
      toast.error("Action failed", { description: err.message });
    } finally {
      setIsAdvancing(false);
    }
  };

  const openCompleteDialog = (stageId: string, stageName: string, nextName?: string) => {
    setConfirmDialog({ type: 'complete', stageId, stageName, nextName });
    setNotes('');
    setClientExplanation('');
  };

  const openRevertDialog = (stageId: string, stageName: string) => {
    setConfirmDialog({ type: 'revert', stageId, stageName });
    setNotes('');
    setClientExplanation('');
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Transaction Timeline</CardTitle>
          <p className="text-sm text-muted-foreground">
            Track the progress of all transaction stages
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-0">
            {sortedStages.map((stage, index) => {
              const status = (stage.status || 'not-started') as keyof typeof statusConfig;
              const config = statusConfig[status] || statusConfig['not-started'];
              const Icon = config.icon;
              const isLast = index === sortedStages.length - 1;
              const docCount = getDocCountForStage(stage.id);
              const isInProgress = status === 'in-progress';
              const canRevert = status === 'completed' && index === lastCompletedIndex;
              const nextStage = !isLast ? sortedStages[index + 1] : null;

              return (
                <div key={stage.id} className="relative">
                  {!isLast && (
                    <div
                      className={`absolute left-5 top-12 w-0.5 ${config.lineClass}`}
                      style={{ height: "calc(100% - 24px)" }}
                    />
                  )}
                  <div
                    className="flex gap-4 p-4 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer group"
                    onClick={() => onStageClick(stage.id)}
                  >
                    <div className={`p-2 rounded-full ${config.iconClass} z-10`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h4 className="font-medium text-foreground group-hover:text-primary transition-colors">
                            {stage.stage_name}
                          </h4>
                          <div className="flex items-center gap-3 mt-1 flex-wrap">
                            <Badge className={config.badge.className}>{config.badge.label}</Badge>
                            {docCount > 0 && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <FileText className="h-3 w-3" />
                                {docCount} documents
                              </span>
                            )}
                          </div>
                          {('internal_notes' in stage) && (stage as any).internal_notes && (
                            <p className="text-sm text-muted-foreground mt-2 flex items-start gap-1">
                              <AlertCircle className="h-3 w-3 mt-1 flex-shrink-0" />
                              {(stage as any).internal_notes}
                            </p>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0 space-y-1">
                          {stage.started_at && (
                            <p className="text-xs text-muted-foreground">
                              Started: {format(new Date(stage.started_at), 'MMM d, yyyy')}
                            </p>
                          )}
                          {stage.completed_at && (
                            <p className="text-xs text-primary">
                              Completed: {format(new Date(stage.completed_at), 'MMM d, yyyy')}
                            </p>
                          )}
                          <div className="flex gap-1 justify-end">
                            {isInProgress && (
                              <Button
                                size="sm"
                                className="mt-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openCompleteDialog(stage.id, stage.stage_name, nextStage?.stage_name);
                                }}
                              >
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Mark Complete
                              </Button>
                            )}
                            {canRevert && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="mt-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openRevertDialog(stage.id, stage.stage_name);
                                }}
                              >
                                <Undo2 className="h-3 w-3 mr-1" />
                                Revert
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={!!confirmDialog} onOpenChange={(open) => { if (!open) setConfirmDialog(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {confirmDialog?.type === 'complete' ? 'Complete Stage' : 'Revert Stage'}
            </DialogTitle>
            <DialogDescription>
              {confirmDialog?.type === 'complete'
                ? confirmDialog.nextName
                  ? `Are you sure you want to complete "${confirmDialog.stageName}"? This will advance the matter to "${confirmDialog.nextName}".`
                  : `Are you sure you want to complete "${confirmDialog?.stageName}"? This is the final stage — the matter will be marked as completed.`
                : `Are you sure you want to revert "${confirmDialog?.stageName}" back to in-progress?`
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Internal Notes (optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={confirmDialog?.type === 'revert' ? "Reason for reverting..." : "Notes about this stage completion..."}
                className="min-h-[80px]"
              />
            </div>
            {confirmDialog?.type === 'complete' && (
              <div className="space-y-2">
                <Label>Client Explanation (optional)</Label>
                <Textarea
                  value={clientExplanation}
                  onChange={(e) => setClientExplanation(e.target.value)}
                  placeholder="Explain to the client what was accomplished..."
                  className="min-h-[80px]"
                />
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmDialog(null)} disabled={isAdvancing}>
              Cancel
            </Button>
            <Button
              onClick={handleAdvance}
              disabled={isAdvancing}
              variant={confirmDialog?.type === 'revert' ? 'outline' : 'default'}
            >
              {isAdvancing ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processing...</>
              ) : confirmDialog?.type === 'complete' ? (
                <><CheckCircle2 className="h-4 w-4 mr-2" />Complete Stage</>
              ) : (
                <><Undo2 className="h-4 w-4 mr-2" />Revert Stage</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
